import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BroadcastTutorialStep } from '../../../../../common/types/broadcast';
import { NoiseCancellationType, VirtualBackgroundType } from '../../../../../common/types/settings';
import {
    FacingMode,
    SecuritySettings,
    SetDeviceActionMeta,
    SettingsState,
    StoreDevice,
    SupportFeedbackForm,
} from './types';

export const refreshDevicesListAction = createAction('@SETTINGS/refreshDevicesList');

export { BroadcastTutorialStep };

/**
 * Валидирует deviceId и возвращает default в случае невалидности
 */
function ensureDeviceId(deviceId: string | null) {
    return deviceId === 'null' || !deviceId ? 'default' : deviceId;
}

export const setAudioOutputDeviceAction = createAction(
    '@SETTINGS/setAudioOutputDevice',
    (deviceId: string | null, meta?: SetDeviceActionMeta) => ({
        payload: { deviceId: ensureDeviceId(deviceId) },
        meta,
    })
);
export const setAudioInputDeviceAction = createAction(
    '@SETTINGS/setAudioInputDevice',
    (deviceId: string | null, meta?: SetDeviceActionMeta) => ({
        payload: { deviceId: ensureDeviceId(deviceId) },
        meta,
    })
);
export const setVideoInputDeviceAction = createAction(
    '@SETTINGS/setVideoInputDevice',
    (deviceId: string | null, meta?: SetDeviceActionMeta) => ({
        payload: { deviceId: ensureDeviceId(deviceId) },
        meta,
    })
);
export const setActiveAudioOutputDeviceAction = createAction<string>('@SETTINGS/setActiveAudioOutputDeviceAction');
export const setActiveAudioInputDeviceAction = createAction<string>('@SETTINGS/setActiveAudioInputDeviceAction');
export const setActiveVideoInputDeviceAction = createAction<string>('@SETTINGS/setActiveVideoInputDevice');
export const setSecuritySettingsAction = createAction<Partial<SecuritySettings>>('@SETTINGS/setSecuritySettings');
export const toggleShowMyVideoSettings = createAction<boolean | undefined>('@SETTINGS/toggleShowMyVideo');
export const toggleCheckDesktopAppExistenceSettings = createAction('@SETTINGS/toggleCheckDesktopAppExistence');
export const saveBroadcastTutorialShownAction = createAction<BroadcastTutorialStep>(
    '@SETTINGS/saveBroadcastTutorialShownAction'
);
export const saveBroadcastCaptionTooltipShownAction = createAction<boolean>(
    '@SETTINGS/saveBroadcastCaptionTooltipShownAction'
);
export const togglePiPActiveAction = createAction<Maybe<boolean>>('@SETTINGS/togglePiPActive');
export const toggleSharingZoomOverrideAction = createAction<Maybe<boolean>>('@SETTINGS/toggleSharingZoomOverride');

export const toggleCameraFacingModeAction = createAction<FacingMode>('@SETTINGS/toggleCameraFacingModeAction');
export const setNoiseCancellationTypeAction = createAction<NoiseCancellationType>(
    '@SETTINGS/setNoiseCancellationTypeAction'
);

export const supportFormWasShownAction = createAction('@SETTINGS/supportFormWasShown');

export const sendSupportFeedbackAction = createAction<SupportFeedbackForm>('@SETTINGS/sendSupportFeedbackAction');

export const toggleJoinWithCameraOffAction = createAction<boolean>('@SETTINGS/toggleJoinWithCameraOff');

export const toggleJoinWithMicrophoneOffAction = createAction<boolean>('@SETTINGS/toggleJoinWithMicrophoneOff');

export const toggleHideParticipantsVideoAction = createAction<boolean>('@SETTINGS/toggleHideParticipantsVideo');

export const toggleBandwidthEffective4kCodecAction = createAction<boolean>('@SETTINGS/toggleBandwidthEffective4kCodec');

export const recordLimitMessageShown = createAction<boolean>('@SETTINGS/recordLimitMessageShown');

export const hideSummarizationWelcomePopupAction = createAction('@SETTINGS/hideSummarizationWelcomePopup');

export const hideSummarizationStartPopupAction = createAction('@SETTINGS/hideSummarizationStartPopup');

export const isSettingsReadyAction = createAction('@SETTINGS/setIsSettingsReady');

export const initialShowMyVideoSetting = true;
export const initialJoinWithCameraOff = false;
export const initialJoinWithMicrophoneOff = false;
export const initialCheckDesktopAppExistence = true;
export const initialIsShownLimitMessage = false;
export const initialNoiseCancellationType = NoiseCancellationType.AUTO;
export const initialHideParticipantsVideo = false;
export const initalVirtualBackgroundType = VirtualBackgroundType.NONE;
export const initialSummarizationWelcomePopupWasShown = false;
export const initialSummarizationStartPopupWasShown = false;
export const initialBandwidthEffective4kCodec = false;
export const initialIsPiPActive = false;
export const initialIsSharingZoomOverrideEnabled = false;

const initialState: SettingsState = {
    security: {
        onlyBetaTesters: false,
        showPromptBeforeConnection: true,
    },

    checkDesktopAppExistence: initialCheckDesktopAppExistence,

    media: {
        isInputDeviceChangeAvailable: false,
        isOutputDeviceChangeAvailable: false,

        devices: [],

        audioOutputDeviceId: '',
        audioInputDeviceId: '',
        videoInputDeviceId: '',

        videoFacingMode: FacingMode.USER,

        showMyVideo: initialShowMyVideoSetting,
        noiseCancellationType: initialNoiseCancellationType,
        joinWithCameraOff: initialJoinWithCameraOff,
        joinWithMicrophoneOff: initialJoinWithMicrophoneOff,
        hideParticipantsVideo: initialHideParticipantsVideo,
        virtualBackgroundType: initalVirtualBackgroundType,
        bandwidthEffective4kCodec: initialBandwidthEffective4kCodec,
    },
    isShownLimitMessage: false,
    isFeedbackFormVisible: false,
    isFaqModalVisible: false,
    isSettingsReady: false,
    isAudioInputDiagnosticRun: false,
    isAudioOutputDiagnosticRun: false,
    wasParticipantsVideoNotificationShown: false,
    summarizationWelcomePopupWasShown: initialSummarizationWelcomePopupWasShown,
    summarizationStartPopupWasShown: initialSummarizationStartPopupWasShown,
    isPiPActive: initialIsPiPActive,
    isSharingZoomOverrideEnabled: initialIsSharingZoomOverrideEnabled,
};

const settingsSlice = createSlice({
    name: '@SETTINGS',
    initialState,
    reducers: {
        setDevicesChangeAvailableAction: (state, action: PayloadAction<{ input: boolean; output: boolean }>) => {
            const { input, output } = action.payload;

            state.media.isInputDeviceChangeAvailable = input;
            state.media.isOutputDeviceChangeAvailable = output;
        },

        setDevicesAction: (state, action: PayloadAction<StoreDevice[]>) => {
            state.media.devices = action.payload;
        },

        setStoreAudioOutputDeviceIdAction: (state, action: PayloadAction<string>) => {
            state.media.audioOutputDeviceId = action.payload;
        },

        setStoreAudioInputDeviceIdAction: (state, action: PayloadAction<string>) => {
            state.media.audioInputDeviceId = action.payload;
        },

        setStoreVideoInputDeviceIdAction: (state, action: PayloadAction<string>) => {
            state.media.videoInputDeviceId = action.payload;
        },

        setStoreSecuritySettingsAction: (state, action: PayloadAction<Partial<SecuritySettings>>) => {
            state.security = {
                ...state.security,
                ...action.payload,
            };
        },

        setStoreShowMyVideoSettingsAction: (state, action: PayloadAction<boolean>) => {
            state.media.showMyVideo = action.payload;
        },
        setStoreCheckDesktopAppExistenceSettingsAction: (state, action: PayloadAction<boolean>) => {
            state.checkDesktopAppExistence = action.payload;
        },

        setIsShownLimitMessageAction: (state, action: PayloadAction<boolean>) => {
            state.isShownLimitMessage = action.payload;
        },

        setVideoFacingModeAction: (state, action: PayloadAction<FacingMode>) => {
            state.media.videoFacingMode = action.payload;
        },
        setStoreNoiseCancellationTypeAction: (state, action: PayloadAction<NoiseCancellationType>) => {
            state.media.noiseCancellationType = action.payload;
        },
        setFeedbackFormVisibility: (state, action: PayloadAction<boolean>) => {
            state.isFeedbackFormVisible = action.payload;
        },
        setIsFaqModalVisible(state, action: PayloadAction<boolean>) {
            state.isFaqModalVisible = action.payload;
        },
        setJoinWithCameraOff: (state, action: PayloadAction<boolean>) => {
            state.media.joinWithCameraOff = action.payload;
        },
        setJoinWithMicrophoneOff: (state, action: PayloadAction<boolean>) => {
            state.media.joinWithMicrophoneOff = action.payload;
        },
        setIsSettingsReady: (state) => {
            state.isSettingsReady = true;
        },
        setIsAudioInputDiagnosticRunAction: (state, action: PayloadAction<boolean>) => {
            state.isAudioInputDiagnosticRun = action.payload;
        },
        setIsAudioOutputDiagnosticRunAction: (state, action: PayloadAction<boolean>) => {
            state.isAudioOutputDiagnosticRun = action.payload;
        },
        setHideParticipantsVideoAction: (state, action: PayloadAction<boolean>) => {
            state.media.hideParticipantsVideo = action.payload;
        },
        setWasParticipantsVideoNotificationShownAction: (state, action: PayloadAction<boolean>) => {
            state.wasParticipantsVideoNotificationShown = action.payload;
        },
        setVirtualBackgroundType: (state, action: PayloadAction<VirtualBackgroundType>) => {
            state.media.virtualBackgroundType = action.payload;
        },
        setSummarizationWelcomePopupWasShown: (state, action: PayloadAction<boolean>) => {
            state.summarizationWelcomePopupWasShown = action.payload;
        },
        setSummarizationStartPopupWasShown: (state, action: PayloadAction<boolean>) => {
            state.summarizationStartPopupWasShown = action.payload;
        },
        setBandwidthEffective4kCodec: (state, action: PayloadAction<boolean>) => {
            state.media.bandwidthEffective4kCodec = action.payload;
        },
        setIsPiPActive: (state, action: PayloadAction<boolean>) => {
            state.isPiPActive = action.payload;
        },
        setIsSharingZoomOverrideEnabled: (state, action: PayloadAction<boolean>) => {
            state.isSharingZoomOverrideEnabled = action.payload;
        },
    },
});

export const {
    setDevicesChangeAvailableAction,
    setDevicesAction,
    setStoreAudioOutputDeviceIdAction,
    setStoreAudioInputDeviceIdAction,
    setStoreVideoInputDeviceIdAction,
    setStoreSecuritySettingsAction,
    setStoreShowMyVideoSettingsAction,
    setStoreCheckDesktopAppExistenceSettingsAction,
    setIsShownLimitMessageAction,
    setVideoFacingModeAction,
    setStoreNoiseCancellationTypeAction,
    setFeedbackFormVisibility,
    setIsFaqModalVisible,
    setJoinWithCameraOff,
    setJoinWithMicrophoneOff,
    setIsSettingsReady,
    setHideParticipantsVideoAction,
    setWasParticipantsVideoNotificationShownAction,
    setVirtualBackgroundType,
    setSummarizationWelcomePopupWasShown,
    setSummarizationStartPopupWasShown,
    setIsAudioInputDiagnosticRunAction,
    setIsAudioOutputDiagnosticRunAction,
    setBandwidthEffective4kCodec,
    setIsPiPActive,
    setIsSharingZoomOverrideEnabled,
} = settingsSlice.actions;

export const settingsReducer = settingsSlice.reducer;
